import {
  Form, App, Row, Col, DatePicker, Select, Input, Typography, Checkbox
} from "antd";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
// import { PREFIXES } from "../../constants/app";
import { Trans, useTranslation } from "react-i18next";
import { ADD_PATIENT_INFORMATION, Gender, GET_REGION_OPTIONS } from "./onboardingModal.qql";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useAppSelector } from "../../store";
import { currentDoctor } from "../../store/slices/user";
import classes from "./onboardingModal.module.css";
import { CodeFiscaleInput, PhoneNumberInput, RegionInput } from "../Form";
import PolicyText from "./policyText";
import { DATE_FORMAT } from "../../constants/date";

const {
  Paragraph,
  Text
} = Typography;

const OnboardingModal = ({ handleToggle }: { handleToggle: () => void }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { data: userData, refetchUser } = useCurrentUser();
  const { message } = App.useApp();
  const doctor = useAppSelector(currentDoctor);
  const [current, setCurrent] = useState(0);
  const [currentValues, setCurrentValues] = useState<any>({});

  // useEffect(() => {
  //   form.setFieldsValue({
  //     phone: {
  //       prefix: PREFIXES[0].value
  //     },
  //   });
  // }, [form]);

  const [addPatientInformation, { loading }] = useMutation(ADD_PATIENT_INFORMATION, {
    fetchPolicy: "no-cache",
    onCompleted(data: { consensoInformato: { id: string } }) {
      if (data && data.consensoInformato?.id) {
        refetchUser();
        handleToggle();
      }
    },
    onError() {
      message.error(t("global.error.fetchError"));
    }
  });

  const { data: regionOptions, loading: regionOptionsLoading } = useQuery(GET_REGION_OPTIONS, {
    fetchPolicy: "cache-first",
  });

  const handleSubmit = (values: any) => {
    if (!doctor) {
      message.error(t("user.error.getDoctor"));
      return;
    }

    setCurrentValues(values);
    setCurrent(current + 1);
  };

  const handleAgreementSubmit = () => {
    if (!doctor) {
      message.error(t("user.error.getDoctor"));
      return;
    }

    const {
      phone,
      birthDate,
      ...rest
    } = currentValues;

    if (currentValues) {
      addPatientInformation({
        variables: {
          professionistId: +doctor.id,
          patientId: +userData.id,
          nation: "IT",
          phone: phone?.number ? phone.number : null,
          birthDate: birthDate.format(DATE_FORMAT.FULL_DATE),
          ...rest
        }
      });
    }
  };

  const data = useMemo(() => {
    return [
      {
        label: t("form.label.name"),
        name: "name",
        rules: [{ required: true, message: t("form.error.name") }],
        layout: 12,
      },
      {
        label: t("form.label.surname"),
        name: "surname",
        rules: [{ required: true, message: t("form.error.surname") }],
        layout: 12,
      },
      {
        label: "Data di nascita",
        name: "birthDate",
        rules: [{ required: true, message: t("form.error.birthDate") }],
        layout: 12,
      },
      {
        label: "Luogo di nascita",
        name: "birthPlace",
        rules: [{ required: true, message: t("form.error.birthPlace") }],
        layout: 12,
      },
      {
        label: t("form.label.phone"),
        name: "phone",
        rules: [{ required: true, message: t("form.error.phone") }],
        layout: 16,
      },
      {
        label: t("form.label.gender"),
        name: "gender",
        rules: [{ required: true, message: t("form.error.gender") }],
        layout: 8,
        options: [
          { value: "", label: null },
          { value: Gender.MALE, label: t("form.gender.male") },
          { value: Gender.FEMALE, label: t("form.gender.female") },
          { value: Gender.PREFER_TO_NOT_ANSWER, label: t("form.gender.notAnswered") },
        ]
      },
      {
        label: t("form.label.address"),
        name: "address",
        rules: [{ required: true, message: t("form.error.address") }],
        layout: 16,
      },
      {
        label: t("form.label.cap"),
        name: "cap",
        rules: [{ required: true, message: t("form.error.cap") }],
        layout: 8,
      },
      {
        label: t("form.label.city"),
        name: "city",
        rules: [{ required: true, message: t("form.error.city") }],
        layout: 12,
      },
      {
        label: t("form.label.province"),
        name: "province",
        rules: [{ required: true, message: t("form.error.province") }],
        layout: 12,
      },
      {
        label: t("form.label.codeFisc"),
        name: "codFisc",
        rules: [{ required: true, message: t("form.error.codFisc") }],
        layout: 24,
      },
    ];
  }, [regionOptions, regionOptionsLoading]);

  const steps = [
    {
      title: <Text>{t("dashboardPage.onboarding.step1Title")}</Text>,
      content: (
        <>
          <div>
            <Paragraph style={{ margin: "16px 0" }}>
              <Trans
                i18nKey="dashboardPage.onboarding.info"
                components={{
                  name: <span>{doctor?.name} {doctor?.surname}</span>,
                  region: <span>{doctor?.alboRegion?.name}</span>,
                  policy: <span>{doctor?.albo_number}</span>,
                }}
              />
            </Paragraph>
          </div>
          <Form
            layout="vertical"
            requiredMark="optional"
            form={form}
            onFinish={handleSubmit}
            className={classes.form}
          >
            <Row gutter={[16, 16]}>
              {data.map((item) => (
                <Col span={24} sm={item.layout} key={item.name}>
                  {(() => {
                    switch (item.name) {
                      case "province":
                        return (
                          <RegionInput
                            name={item.name}
                            label={item.label}
                            rules={item.rules}
                          />
                        );
                      case "phone":
                        return (
                          <PhoneNumberInput name={item.name} label={item.label}/>
                        );
                      case "birthDate":
                        return (
                          <Form.Item
                            label={item.label}
                            name={item.name}
                            rules={item.rules}
                          >
                            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                          </Form.Item>
                        );
                      case "codFisc":
                        return <CodeFiscaleInput name={item.name} label={item.label}/>;
                      case "gender":
                        return (
                          <Form.Item
                            label={item.label}
                            name={item.name}
                          >
                            <Select options={item.options}/>
                          </Form.Item>
                        );
                      default:
                        return (
                          <Form.Item
                            label={item.label}
                            name={item.name}
                            rules={item.rules}
                          >
                            <Input/>
                          </Form.Item>
                        );
                    }
                  })()}
                </Col>
              ))}
            </Row>
          </Form>
        </>
      )
    },
    {
      title: <Text>{t("dashboardPage.onboarding.step2Title")}</Text>,
      content:
  <>
    <PolicyText formValues={currentValues} />
    <Form
      layout="vertical"
      requiredMark="optional"
      form={form}
      onFinish={handleAgreementSubmit}
    >
      <Form.Item
        name="agreement"
        valuePropName="checked"
        rules={[{ required: true, message: t("form.error.agreement") }]}
      >
        <Checkbox>
          {t("form.label.agreement")}
        </Checkbox>
      </Form.Item>
    </Form>
  </>
      ,
    }
  ];

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  const prevButtonText = () => {
    switch (current) {
      case 0:
        return t("global.button.cancel");
      default:
        return t("global.button.prev");
    }
  };

  const nextButtonText = () => {
    switch (current) {
      case steps.length - 1:
        return t("global.button.confirm");
      default:
        return t("global.button.next");
    }
  };

  const handlePrevClick = () => {
    if (current === 0) {
      handleToggle();
    } else {
      setCurrent(current - 1);
    }
  };

  const handleNextClick = () => {
    form.submit();
  };

  return {
    loading,
    data,
    doctor,
    current,
    steps,
    items,
    prevButtonText,
    nextButtonText,
    handlePrevClick,
    handleNextClick
  };
};

export default OnboardingModal;
